import type {
    MappedProductVariant,
    ProductHit,
    ProductVariantInventory,
    SelectedVariantKeys,
    SelectedVariants,
    UniqueVariants,
    VariantValue,
    ProductVariant,
} from '@libs/algolia';
import { SelectedValueKey } from '@libs/algolia';
import { LOCALIZED } from '@libs/core';
import pick from 'lodash.pick';

const ascAlphabeticSort = (a: VariantValue, b: VariantValue) =>
    a.display && b.display ? a.display.localeCompare(b.display) : 0;

const mapColorToVariantValue = (variant: MappedProductVariant) =>
    ({
        key: variant.color?.value,
        value: variant.color?.value,
        display: variant.color?.swatchDisplay,
    }) as VariantValue;

const filterOutStyleKeys = (selectedVariants: SelectedVariants) =>
    pick(selectedVariants, Object.values(SelectedValueKey));

const filterOutColorKeys = (selectedVariants: SelectedVariants) => {
    const { color: _color, ...filteredSelectedVariats } =
        filterOutStyleKeys(selectedVariants);

    return filteredSelectedVariats;
};

export const getSelectedProduct = (
    selectedVariants: SelectedVariants,
    variants: MappedProductVariant[],
    product: ProductHit
): MappedProductVariant => {
    const filteredSelectedVariants = filterOutStyleKeys(selectedVariants);
    const filteredVariants = Object.keys(filteredSelectedVariants);

    const foundVariant: MappedProductVariant | undefined = variants.find(item =>
        filteredVariants.every(
            key =>
                item.variants?.[key]?.value ===
                selectedVariants[key as SelectedValueKey]
        )
    );

    const fallbackVariant = variants.find(
        variant => variant.objectID === product.objectID
    );

    return foundVariant ?? fallbackVariant ?? variants[0];
};

export const getAvailableColors = (
    selectedVariants: SelectedVariants,
    variants: MappedProductVariant[]
) => {
    const filteredVariantKeys = Object.keys(
        filterOutColorKeys(selectedVariants)
    );
    const color = variants
        .filter(variant =>
            filteredVariantKeys.length > 0
                ? filteredVariantKeys.every(
                      key =>
                          variant?.color?.value &&
                          selectedVariants?.[key as SelectedValueKey] ===
                              variant.variants?.[key]?.value
                  )
                : !!variant?.color?.value
        )
        .map(mapColorToVariantValue)
        .sort(ascAlphabeticSort);

    return color.length > 0 ? { color } : {};
};

export const getSelectedVariant = (
    mappedVariant: MappedProductVariant,
    uniqueVariants: UniqueVariants,
    personalizationFields: Partial<Record<SelectedVariantKeys, VariantValue[]>>
): SelectedVariants => {
    const variation: SelectedVariants = {} as SelectedVariants;
    Object.entries(uniqueVariants).forEach(([key, variantValues]) => {
        variation[key as keyof typeof variation] =
            mappedVariant.variants?.[key]?.value ?? variantValues[0]?.value;
    });

    // For personalization fields ,the first value will be the default value
    Object.entries(personalizationFields).forEach(([key, [firstValue]]) => {
        variation[key as keyof typeof variation] = firstValue.value;
    });

    return variation;
};

const petSizeOrderLookup = Object.fromEntries(
    [
        'one size',
        '3X Small',
        '2X Small',
        'X Small',
        'X Small/Small',
        'Small',
        'Small Long',
        'Small/Medium',
        'Medium',
        'Dog - Medium',
        'Medium Extra Long',
        'Regular',
        'Medium/Large',
        'Large',
        'Large/X Large',
        'X Large',
        '2X Large',
        '3X Large',
        'Unisex Human - Small',
        'Unisex Human - Medium',
        'Unisex Human - Large',
        'Kitten',
        'Cat (Adult)',
        'Mini Pellet',
        'Pellet',
    ].map((e, i) => [e, i])
);

const nPetSizes = Object.keys(petSizeOrderLookup).length;

/**
 * Sorts incoming pet size data in ascending order. A petSize is either a
 * string like 'X Small' or 'Large', but could also be a single number 1 .. n.
 * If lookup fails and it is not a number, element is put unsorted at end.
 */
export const ascPetSizeSort = (a: VariantValue, b: VariantValue) => {
    const ai = petSizeOrderLookup[a.value] ?? (Number(a.value) || nPetSizes);
    const bi = petSizeOrderLookup[b.value] ?? (Number(b.value) || nPetSizes);

    return ai - bi;
};

/**
 * Splits on x and X, trims each element, and removes the L/W/H/D from the end.
 */
export const getDimensions = (variant: VariantValue) =>
    variant.value.split(/[xX]/g).map(s => s.trim().slice(0, -1));

/**
 * Converts incoming inches or feet values to inches..
 */
export const toInches = (s: string) => {
    const value = parseFloat(s);
    return s.endsWith('"') ? value : 12 * value;
};

/**
 * Volume reducer function - returns the product of a and b.
 */
const toVolume = (a: number, b: number) => a * b;

/**
 * Sorts incoming dimensional data in ascending order with the expectation
 * that twoDimensional and threeDimensional input is not mixed.
 * @returns
 */
export const ascDimensionalSort = (a: VariantValue, b: VariantValue) => {
    const aDimensions = getDimensions(a);
    const bDimensions = getDimensions(b);

    if (!aDimensions || !bDimensions) {
        return 0;
    }

    const aVolume = aDimensions.map(toInches).reduce(toVolume);
    const bVolume = bDimensions.map(toInches).reduce(toVolume);

    return aVolume - bVolume;
};

export const toMilligrams = (s: string) => {
    const [value, ...unit] = s.split(' ');
    const amount = Number(value);

    switch (unit.join(' ').toLowerCase()) {
        case 'oz':
            return amount * 28_349.5;
        case 'g':
            return amount * 1000;
        case 'kg':
            return amount * 1000 * 1000;
        case 'lb':
        case 'lbs':
            return amount * 453_592;
        case 'cup':
            return amount * 236_588;
        case 'ft':
            return amount * 13; // compared to inches below
        case 'in': // inches is a solid size for some reason
        case 'ct': // count (not carat) is also a solid size
        case 'count':
        case 'mg':
        default:
            return amount;
    }
};

export const toMillilitres = (s: string) => {
    const [value, ...unit] = s.split(' ');
    const amount = Number(value);

    switch (unit.join(' ').toLowerCase()) {
        case 'l':
            return amount * 1_000;
        case 'dl':
            return amount * 100;
        case 'cl':
            return amount * 10;
        case 'cup':
            return amount * 236.588;
        case 'gal':
            return amount * 3_785.41;
        case 'fl oz':
            return amount * 29.5735296;
        case 'qt':
            return amount * 946.352946;
        case 'ml':
        default:
            return amount;
    }
};

const solidTextSizes = new Set(Object.keys(petSizeOrderLookup));

/**
 * Sorts incoming solid data in ascending order.
 */
export const ascSolidSort = (a: VariantValue, b: VariantValue) => {
    if (solidTextSizes.has(a.value) && solidTextSizes.has(b.value)) {
        // petSize values like Small and Large can show up as solid sizes.
        return ascPetSizeSort(a, b);
    }

    return toMilligrams(a.value) - toMilligrams(b.value);
};

/**
 * Sorts incoming fluid data in ascending order.
 */
export const ascFluidSort = (a: VariantValue, b: VariantValue) =>
    toMillilitres(a.value) - toMillilitres(b.value);

/**
 * Sorts mono unit variants like quantity and wattage.
 */
export const ascMonoUnitSort = (a: VariantValue, b: VariantValue) =>
    Number(a.value.split(' ')[0]) - Number(b.value.split(' ')[0]);

/**
 * Sorts the size array in-place in ascending order.
 * Expects all sizes to have the same type (prefix),
 * i.e., no combinations like 1 fluidSize and 1 solidSize.
 */
export const sortSizes = (sizes: VariantValue[]) => {
    if (sizes.length === 0) {
        return;
    }

    const [sizeType] = sizes[0].key.split('-');
    if (!sizeType.endsWith('Size')) {
        return;
    }

    switch (sizeType) {
        case 'petSize':
            sizes.sort(ascPetSizeSort);
            break;
        case 'twoDimensionalSize':
        case 'threeDimensionalSize':
            sizes.sort(ascDimensionalSort);
            break;
        case 'solidSize':
            sizes.sort(ascSolidSort);
            break;
        case 'fluidSize':
            sizes.sort(ascFluidSort);
            break;
        default:
    }
};

/**
 * Sorts all variants.
 */
export const sortVariants = (variants: UniqueVariants) => {
    try {
        Object.entries(variants).forEach(([key, value]) => {
            switch (key) {
                case 'size':
                    sortSizes(value);
                    break;
                case 'wattage':
                case 'quantity':
                    value.sort(ascMonoUnitSort);
                    break;
                default:
                    value.sort(ascAlphabeticSort);
            }
        });
    } catch (_) {
        /* There is no need to handle errors here. */
    }
};

export const isVariantInStock = (
    product: ProductHit,
    inventories?: ProductVariantInventory
) => {
    const inStock = {
        bopis: false,
        sdd: false,
        web: false,
    };
    const productInventory = inventories?.[product.objectID];

    if (!productInventory) {
        return inStock;
    }

    const hasEnoughBopisQty =
        productInventory.ats > (product?.bopisCaseQty || 1);

    if (hasEnoughBopisQty) {
        inStock.bopis = true;
    }

    const isWebInventory = productInventory.id === LOCALIZED.webInventory;
    const hasEnoughWebInventoryQty =
        isWebInventory &&
        productInventory.ats > (product?.customCaseQuantity || 1);

    if (hasEnoughWebInventoryQty) {
        inStock.web = true;
    }

    return inStock;
};

export const getVariantIds = (variants: ProductVariant[]) =>
    variants.map(variant => variant.id.toString());
